// Internal accounts (billing_exempt). Confirmed @speak.ad addresses default
// to exempt. An unverified email never does. A missing column means billing
// stays on, so a deploy before the SQL file does not stop charging customers.
// Deno-free so Vitest and the React app can import it.

import { canStartCreditCalls } from "./billingPrices.ts";

export const SPEAK_AD_EMAIL_DOMAIN = "speak.ad";

export const INTERNAL_BILLING_BANNER = "Internal account — billing off";

/** Domain of an address with exactly one @. Anything else is not speak.ad. */
export function emailDomain(email: string | null | undefined): string | null {
  if (!email) return null;
  const trimmed = email.trim().toLowerCase();
  const parts = trimmed.split("@");
  if (parts.length !== 2 || !parts[0] || !parts[1]) return null;
  return parts[1];
}

/**
 * True only when the auth email is confirmed and the domain is exactly speak.ad.
 * Do not pass profiles.email. Unconfirmed addresses stay billed.
 */
export function confirmedSpeakAdExempt(input: {
  email?: string | null;
  emailConfirmedAt?: string | Date | null;
}): boolean {
  const confirmedAt = input.emailConfirmedAt;
  if (confirmedAt == null || confirmedAt === "") return false;
  if (confirmedAt instanceof Date && Number.isNaN(confirmedAt.getTime())) return false;
  return emailDomain(input.email) === SPEAK_AD_EMAIL_DOMAIN;
}

/** Only a real boolean true is exempt. Missing, null, and false keep billing on. */
export function normalizeBillingExempt(value: unknown): boolean {
  return value === true;
}

export function planCreditCallStart(input: {
  billingExempt: boolean;
  balance: number;
  callCount?: number;
  creditsPerMinute: number;
  holdCredits: number;
}): { allowed: boolean; holdCredits: number; required: number } {
  if (input.billingExempt) {
    return { allowed: true, holdCredits: 0, required: 0 };
  }
  const calls = input.callCount ?? 1;
  const gate = canStartCreditCalls(input.balance, calls, input.creditsPerMinute);
  if (!gate.ok) {
    return { allowed: false, holdCredits: 0, required: gate.required };
  }
  const holdEach = Number.isFinite(input.holdCredits) ? Math.max(0, input.holdCredits) : 0;
  return { allowed: true, holdCredits: holdEach, required: gate.required };
}

/** Hold placed before dial. Exempt accounts reserve nothing. */
export function creditHoldForStart(billingExempt: boolean, holdCredits: number): number {
  if (billingExempt) return 0;
  return Number.isFinite(holdCredits) ? Math.max(0, holdCredits) : 0;
}

/** Batch pre-check. Exempt accounts are never blocked on balance. */
export function creditBalanceBlocksStart(input: {
  billingExempt: boolean;
  balance: number;
  callCount: number;
  creditsPerMinute: number;
}): boolean {
  if (input.billingExempt) return false;
  return !canStartCreditCalls(input.balance, input.callCount, input.creditsPerMinute).ok;
}

/**
 * Usage is always recorded. Exempt accounts do not move the credit balance
 * and do not create a Stripe shortfall.
 */
export function planUsageSettlement(input: {
  billingExempt: boolean;
  billableCredits: number;
  callCostGbp: number;
  creditsPerMinute: number;
  delta: number;
  shortfall: number;
}): {
  creditsUsed: number;
  callCostGbp: number;
  rateApplied: number;
  balanceDelta: number;
  stripeShortfallCredits: number;
} {
  const shortfall = Number.isFinite(input.shortfall) ? Math.max(0, input.shortfall) : 0;
  return {
    creditsUsed: input.billableCredits,
    callCostGbp: input.callCostGbp,
    rateApplied: input.creditsPerMinute,
    balanceDelta: input.billingExempt ? 0 : input.delta,
    stripeShortfallCredits: input.billingExempt ? 0 : shortfall,
  };
}

/** Pro checkout is not required for an internal account to claim or buy a number. */
export function proCheckoutRequired(input: { billingExempt: boolean; isPro: boolean }): boolean {
  if (input.billingExempt) return false;
  return input.isPro !== true;
}

// deno-lint-ignore no-explicit-any
type SupabaseAny = any;

/** Missing column or a failed read stays billed (exempt = false). */
export async function loadBillingExempt(supabase: SupabaseAny, userId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from("subscribers")
      .select("billing_exempt")
      .eq("user_id", userId)
      .maybeSingle();
    if (error) return false;
    return data?.billing_exempt === true;
  } catch {
    return false;
  }
}
